import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import { MarkAsFulfilledWithDataInput } from "@redotech/redo-merchant-fulfillment-rpc/schema/mark-as-fulfilled-with-data";
import {
  Carriers,
  carrierToLabel,
} from "@redotech/redo-model/fulfillments/fulfillment-carriers-and-services";
import { FulfillmentGroupData } from "@redotech/redo-model/fulfillments/fulfillment-group";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoSingleDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-single-date-picker";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoBaseModal } from "@redotech/redo-web/arbiter-components/modal/redo-base-modal";
import { RedoModalHeader } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { DisabledTooltip } from "@redotech/redo-web/tooltip/disabled-tooltip";
import {
  dateToPlainDateTime,
  plainDateTimeToDate,
} from "@redotech/util/temporal";
import { memo, useEffect, useRef } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";
import { useContainsTiktokShippingOrder } from "../../utils/use-group-info";
import {
  defaultBulkUpdate,
  fulfillmentGroupToRowInput,
  FulfillmentModalRowInput,
  markAsFulfilledForm,
  MarkAsFulfilledFormValue,
  ProvidersRequiringTrackingForFulfillment,
} from "./mark-as-fulfilled-form";
import { MarkAsFulfilledModalTable } from "./mark-as-fulfilled-modal-table";
import * as styles from "./mark-as-fulfilled-modal.module.css";

export const carrierOptions: RedoListItem<Carriers>[] = Object.values(
  Carriers,
).map((carrier) => ({
  id: carrier,
  value: carrier,
  type: "text",
  text: carrierToLabel[carrier],
}));

const notifyCustomerOptions: RedoListItem<boolean>[] = [
  { id: "notify-customer", value: true, type: "text", text: "Notify customer" },
  {
    id: "do-not-notify-customer",
    value: false,
    type: "text",
    text: "Do not notify customer",
  },
];

const notifyMarketplaceOptions: RedoListItem<boolean>[] = [
  {
    id: "notify-marketplace",
    value: true,
    type: "text",
    text: "Notify marketplace",
  },
  {
    id: "do-not-notify-marketplace",
    value: false,
    type: "text",
    text: "Do not notify marketplace",
  },
];

const formDataToInput = (
  formData: MarkAsFulfilledFormValue,
): MarkAsFulfilledWithDataInput => {
  return formData.rowData.map((row) => ({
    fulfillmentGroupId: row.fulfillmentGroupId,
    shipDate: row.shipDate,
    shippingSelection: row.shippingSelection,
    fulfillmentDate: formData.bulkUpdate.shipDate,
    trackingNumber: row.trackingNumber,
    notifyMarketplace: row.notifyMarketplace,
    notifyCustomer: row.notifyCustomer,
  }));
};

export const MarkAsFulfilledModal = memo(function MarkAsFulfilledModal({
  open,
  onClose,
  groups,
  onGroupsFulfillment,
}: {
  open: boolean;
  onClose: () => void;
  groups: readonly FulfillmentGroupData[];
  onGroupsFulfillment?: (groupIds: string[]) => void;
}) {
  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);

  const defaultFormValue: MarkAsFulfilledFormValue = {
    defaultValue: "",
    bulkUpdate: defaultBulkUpdate,
    rowData: groups.map((group) => fulfillmentGroupToRowInput(group)),
  };

  const form = useInput(markAsFulfilledForm, defaultFormValue);

  const containsTiktokShippingOrder = useContainsTiktokShippingOrder(groups);

  const formInitializedRef = useRef(false);
  useEffect(() => {
    if (groups.length > 0 && !formInitializedRef.current) {
      formInitializedRef.current = true;
      form.setValue({
        ...form.value,
        rowData: groups.map((group) => fulfillmentGroupToRowInput(group)),
      });
    }
  }, [groups, form]);

  const setBulkUpdate = useHandler(
    (
      key: keyof MarkAsFulfilledFormValue["bulkUpdate"],
      value: MarkAsFulfilledFormValue["bulkUpdate"][keyof MarkAsFulfilledFormValue["bulkUpdate"]],
    ) => {
      const newValue = {
        ...form.value,
        bulkUpdate: { ...form.value.bulkUpdate, [key]: value },
        rowData: form.value.rowData.map((row) => ({ ...row, [key]: value })),
      };

      // Don't let the user try to notify the customer without also notifying the marketplace
      if (key === "notifyMarketplace" && value === false) {
        newValue.bulkUpdate.notifyCustomer = false;
        newValue.rowData = newValue.rowData.map((row) => ({
          ...row,
          notifyCustomer: false,
        }));
      } else if (key === "notifyCustomer" && value === true) {
        newValue.rowData = newValue.rowData.map((row) => ({
          ...row,
          notifyCustomer: row.notifyMarketplace && value,
        }));
      }

      form.setValue(newValue);
    },
  );

  const rowUpdate = useHandler(
    (index: number, update: Partial<FulfillmentModalRowInput>) => {
      if (update.notifyMarketplace === false) {
        update.notifyCustomer = false;
      }

      if (
        (update.trackingNumber === "" || update.shippingSelection === null) &&
        ProvidersRequiringTrackingForFulfillment.some((provider) =>
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          form.value.rowData[index].providers?.includes(provider),
        )
      ) {
        update.notifyMarketplace = false;
        update.notifyCustomer = false;
      }

      form.setValue({
        ...form.value,
        rowData: form.value.rowData.map((row, i) =>
          i === index ? { ...row, ...update } : row,
        ),
      });
    },
  );

  const [markFulfilledLoad, doMarkFulfilled] = useTriggerLoad(async () => {
    try {
      const data: MarkAsFulfilledWithDataInput = formDataToInput(form.value);

      if (
        data.some((d) => d.notifyMarketplace && containsTiktokShippingOrder)
      ) {
        toast("Cannot mark TikTok Shipping orders as fulfilled", {
          variant: "error",
        });
        return;
      }
      if (onGroupsFulfillment) {
        onGroupsFulfillment(groups.map((g) => g._id));
      }

      await client.markAsFulfilledWithData(data);
      toast("Marked as fulfilled", {
        variant: "success",
        duration: Temporal.Duration.from({ seconds: 3 }),
      });
    } catch (error) {
      console.error(error);
      toast("Error marking as fulfilled", { variant: "error" });
    } finally {
      onClose();
    }
  });

  return (
    <RedoBaseModal
      isOpen={open}
      onModalCloseRequested={() => {
        onClose();
      }}
      otherClasses={[styles.shipmentModal]}
    >
      <RedoModalHeader
        cancelClicked={onClose}
        headerBorder
        title="Mark as fulfilled"
      />
      <Flex
        className={styles.shipmentModalContent}
        dir="column"
        gap="none"
        grow={1}
        w="full"
      >
        <Flex align="flex-start" dir="row" gap="md" pb="md" pl="2xl">
          <Text fontSize="sm" textColor="primary">
            Bulk update
          </Text>
        </Flex>
        <Flex dir="row" gap="none" pb="md" w="full">
          <Flex dir="row" gap="xl" pb="sm" px="2xl">
            <Flex
              className={styles.shipDateInput}
              dir="column"
              gap="sm"
              p="none"
            >
              <RedoSingleDatePicker
                buttonSize="sm"
                date={dateToPlainDateTime(
                  form.value.bulkUpdate.shipDate,
                ).toPlainDate()}
                dateStringOptions={{ year: undefined }}
                maxDate={dateToPlainDateTime(new Date()).toPlainDate()}
                setDate={(plainDate) => {
                  setBulkUpdate(
                    "shipDate",
                    plainDateTimeToDate(plainDate.toPlainDateTime()),
                  );
                }}
              />
            </Flex>
            <RedoSingleSelectDropdownInput<Carriers>
              className={styles.fulfillmentInput}
              disabled={false}
              options={carrierOptions}
              optionSelected={({ item: carrier }) => {
                setBulkUpdate("shippingSelection", carrier.value);
              }}
              placeholder="Select carrier"
              selectedItem={carrierOptions.find(
                (carrier) =>
                  carrier.value === form.value.bulkUpdate.shippingSelection,
              )}
              size="xs"
            />
            <DisabledTooltip
              disabledMessage={
                !form.value.bulkUpdate.notifyMarketplace
                  ? "Cannot notify customer without notifying marketplace"
                  : undefined
              }
            >
              <RedoSingleSelectDropdownInput<boolean>
                className={styles.fulfillmentInput}
                disabled={!form.value.bulkUpdate.notifyMarketplace}
                options={notifyCustomerOptions}
                optionSelected={({ item: notifyCustomer }) => {
                  setBulkUpdate("notifyCustomer", notifyCustomer.value);
                }}
                selectedItem={notifyCustomerOptions.find(
                  (notifyCustomer) =>
                    notifyCustomer.value ===
                    form.value.bulkUpdate.notifyCustomer,
                )}
                size="xs"
              />
            </DisabledTooltip>
            <RedoSingleSelectDropdownInput<boolean>
              className={styles.fulfillmentInput}
              disabled={false}
              options={notifyMarketplaceOptions}
              optionSelected={({ item: notifyMarketplace }) => {
                setBulkUpdate("notifyMarketplace", notifyMarketplace.value);
              }}
              selectedItem={notifyMarketplaceOptions.find(
                (notifyMarketplace) =>
                  notifyMarketplace.value ===
                  form.value.bulkUpdate.notifyMarketplace,
              )}
              size="xs"
            />
          </Flex>
        </Flex>
        <Flex className={styles.tableContainer} w="full">
          <MarkAsFulfilledModalTable doUpdate={rowUpdate} form={form} />
        </Flex>
        <Flex
          align="center"
          borderColor="primary"
          borderStyle="solid"
          borderTopWidth="1px"
          className={styles.footer}
          dir="row"
          justify="flex-end"
          p="3xl"
          w="full"
        >
          <RedoButton
            hierarchy="secondary"
            onClick={onClose}
            size="md"
            text="Cancel"
            theme="normal"
          />
          <RedoButton
            hierarchy="primary"
            onClick={() => {
              doMarkFulfilled();
            }}
            pending={markFulfilledLoad.pending}
            size="md"
            text="Mark as fulfilled"
            theme="success"
            type="submit"
          />
        </Flex>
      </Flex>
    </RedoBaseModal>
  );
});
